import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './minesweeper.css';

type Cell = {
  row: number;
  col: number;
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  adjacent: number; // number of adjacent mines
};

type GameState = 'playing' | 'won' | 'lost';

function generateBoard(rows: number, cols: number, mines: number, safeRow?: number, safeCol?: number): Cell[][] {
  // Create empty board
  const board: Cell[][] = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => ({
      row: r,
      col: c,
      isMine: false,
      isRevealed: false,
      isFlagged: false,
      adjacent: 0,
    }))
  );

  // Helper to list neighbors
  const neighbors = (r: number, c: number) => {
    const res: [number, number][] = [];
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr;
        const nc = c + dc;
        if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) res.push([nr, nc]);
      }
    }
    return res;
  };

  // Place mines (avoid the first clicked cell if provided)
  let placed = 0;
  while (placed < mines) {
    const r = Math.floor(Math.random() * rows);
    const c = Math.floor(Math.random() * cols);
    if (board[r][c].isMine) continue;
    if (safeRow !== undefined && safeCol !== undefined) {
      if (r === safeRow && c === safeCol) continue;
      // Optionally avoid first ring as well for nicer starts
      if (Math.abs(r - safeRow) <= 1 && Math.abs(c - safeCol) <= 1) continue;
    }
    board[r][c].isMine = true;
    placed++;
  }

  // Compute adjacent counts
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (board[r][c].isMine) continue;
      const adj = neighbors(r, c).reduce((acc, [nr, nc]) => acc + (board[nr][nc].isMine ? 1 : 0), 0);
      board[r][c].adjacent = adj;
    }
  }

  return board;
}

function revealZeros(board: Cell[][], startR: number, startC: number): void {
  const rows = board.length;
  const cols = board[0].length;
  const inBounds = (r: number, c: number) => r >= 0 && r < rows && c >= 0 && c < cols;

  const queue: [number, number][] = [[startR, startC]];
  while (queue.length) {
    const [r, c] = queue.shift()!;
    const cell = board[r][c];
    if (cell.isRevealed || cell.isFlagged) continue;
    cell.isRevealed = true;
    if (cell.adjacent === 0 && !cell.isMine) {
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (inBounds(nr, nc) && !board[nr][nc].isRevealed && !board[nr][nc].isMine) {
            queue.push([nr, nc]);
          }
        }
      }
    }
  }
}

const Minesweeper: React.FC = () => {
  const [rows, setRows] = useState(9);
  const [cols, setCols] = useState(9);
  const [mines, setMines] = useState(10);
  const [board, setBoard] = useState<Cell[][]>(() => generateBoard(9, 9, 10));
  const [state, setState] = useState<GameState>('playing');
  const [firstClick, setFirstClick] = useState<boolean>(true);

  const flagsLeft = useMemo(() => {
    const flagged = board.flat().filter((c) => c.isFlagged).length;
    return Math.max(0, mines - flagged);
  }, [board, mines]);

  const revealedSafeCount = useMemo(() => board.flat().filter((c) => c.isRevealed && !c.isMine).length, [board]);
  const totalSafe = useMemo(() => rows * cols - mines, [rows, cols, mines]);

  useEffect(() => {
    if (state === 'playing' && revealedSafeCount === totalSafe) {
      setState('won');
      // Reveal all mines as flagged for effect
      setBoard((prev) => prev.map((r) => r.map((c) => (c.isMine ? { ...c, isFlagged: true } : c))));
    }
  }, [revealedSafeCount, totalSafe, state]);

  const reset = useCallback(() => {
    setBoard(generateBoard(rows, cols, mines));
    setState('playing');
    setFirstClick(true);
  }, [rows, cols, mines]);

  const onCellClick = useCallback(
    (r: number, c: number) => {
      if (state !== 'playing') return;
      setBoard((prev) => {
        let next = prev.map((row) => row.map((cell) => ({ ...cell })));
        // Ensure first click is safe: regenerate with safe cell
        if (firstClick) {
          next = generateBoard(rows, cols, mines, r, c);
        }
        const cell = next[r][c];
        if (cell.isFlagged || cell.isRevealed) return next;
        if (cell.isMine) {
          cell.isRevealed = true;
          setState('lost');
          // Reveal all mines
          next = next.map((row) => row.map((c) => (c.isMine ? { ...c, isRevealed: true } : c)));
          return next;
        }
        if (cell.adjacent === 0) {
          revealZeros(next, r, c);
        } else {
          cell.isRevealed = true;
        }
        if (firstClick) setFirstClick(false);
        return next;
      });
    },
    [state, firstClick, rows, cols, mines]
  );

  const onRightClick = useCallback(
    (e: React.MouseEvent, r: number, c: number) => {
      e.preventDefault();
      if (state !== 'playing') return;
      setBoard((prev) => {
        const next = prev.map((row) => row.map((cell) => ({ ...cell })));
        const cell = next[r][c];
        if (cell.isRevealed) return next;
        if (!cell.isFlagged && flagsLeft <= 0) return next;
        cell.isFlagged = !cell.isFlagged;
        return next;
      });
    },
    [state, flagsLeft]
  );

  const difficultyPresets = [
    { label: 'Beginner (9x9, 10 mines)', r: 9, c: 9, m: 10 },
    { label: 'Intermediate (16x16, 40 mines)', r: 16, c: 16, m: 40 },
    { label: 'Expert (30x16, 99 mines)', r: 16, c: 30, m: 99 },
  ];

  const applyPreset = (r: number, c: number, m: number) => {
    setRows(r);
    setCols(c);
    setMines(m);
    setBoard(generateBoard(r, c, m));
    setState('playing');
    setFirstClick(true);
  };

  return (
    <div className="ms-container">
      <div className="ms-toolbar">
        <div className={`ms-status ${state}`}>{state.toUpperCase()}</div>
        <div className="ms-controls">
          <select onChange={(e) => {
            const idx = Number(e.target.value);
            const p = difficultyPresets[idx];
            applyPreset(p.r, p.c, p.m);
          }}>
            {difficultyPresets.map((p, i) => (
              <option key={i} value={i}>{p.label}</option>
            ))}
          </select>
          <button onClick={reset}>Reset</button>
          <span>Mines left: {flagsLeft}</span>
        </div>
      </div>

      <div className="ms-board" style={{
        gridTemplateColumns: `repeat(${cols}, 28px)`,
        gridTemplateRows: `repeat(${rows}, 28px)`
      }}>
        {board.map((row, r) =>
          row.map((cell, c) => {
            const classNames = [
              'ms-cell',
              cell.isRevealed ? 'revealed' : '',
              cell.isFlagged ? 'flagged' : '',
              cell.isMine && state === 'lost' ? 'mine' : '',
              cell.adjacent ? `n${cell.adjacent}` : '',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={`${r}-${c}`}
                className={classNames}
                onClick={() => onCellClick(r, c)}
                onContextMenu={(e) => onRightClick(e, r, c)}
                aria-label={`row ${r + 1} col ${c + 1}`}
              >
                {cell.isRevealed && !cell.isMine && cell.adjacent > 0 ? cell.adjacent : ''}
                {cell.isFlagged && !cell.isRevealed ? '🚩' : ''}
                {cell.isRevealed && cell.isMine ? '💣' : ''}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Minesweeper;